import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
	Box,
	Button, Chip,
	Container, Dialog,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	Popover,
	Switch,
	TextField, Tooltip,
	Typography,
	useTheme
} from '@mui/material'
import ImageOutlinedIcon                     from '@mui/icons-material/ImageOutlined'
import ImageIcon                             from '@mui/icons-material/Image'
import AudiotrackOutlinedIcon                from '@mui/icons-material/AudiotrackOutlined'
import AudiotrackIcon                        from '@mui/icons-material/Audiotrack'
import MovieOutlinedIcon                     from '@mui/icons-material/MovieOutlined'
import MovieIcon                             from '@mui/icons-material/Movie'

import DeleteOutlineIcon  from '@mui/icons-material/DeleteOutline'
import FolderIcon                   from '@mui/icons-material/Folder'
import {doDelete, doGet, getApiUrl} from '../../util/do-fetch'
import ConfirmDialog                from '../ConfirmDialog'
import {useOutletContext} from 'react-router-dom'
import {abcd}              from "../../util/prepkit-utils"
import {Close, Fullscreen} from "@mui/icons-material"
import {convertToRgba} from "../../util/utils"
import {text} from "framer-motion/m"

const hasAnyCache = row =>
	row?.image?.local ||
	row?.image?.remote ||
	Object.values(row?.variants || {}).some(v =>
		v?.audio?.local || v?.audio?.remote || v?.video?.local || v?.video?.remote
	)

const getFieldValue = (row, obj) => {
	// TTS always wins
	if (row?.tts) {
		return row.tts
	}

	if (row.entity === 'question') {
		switch (row.field) {
			case 'question':
				return obj.question
			case 'answer':
				return abcd(obj.answer)
			case 'option-a':
				return obj.options[0]?.label
			case 'option-b':
				return obj.options[1]?.label
			case 'option-c':
				return obj.options[2]?.label
			case 'option-d':
				return obj.options[3]?.label
			case 'explanation':
				return obj.explanation
			default:
				return ''
		}
	}

	if (row.entity === 'topic') {
		switch (row.field) {
			case 'section':
			case 'title':
				return obj.name
			default:
				return ''
		}
	}

	return ''
}

const ProductMediaCache = () => {
	const {product} = useOutletContext()
	const theme = useTheme()

	const [data, setData] = useState(null)
	const [filter, setFilter] = useState('')
	const [onlyCached, setOnlyCached] = useState(false)
	const [confirm, setConfirm] = useState(null)
	const [preview, setPreview] = useState(null)
	const [fullscreen, setFullscreen] = useState(false)

	const openPreview = async (e, type, src, isLocal) => {
		const anchorEl = e.currentTarget

		setPreview({
			anchorEl,
			type,
			mediaUrl: null,
			loading: true
		})

		let mediaUrl

		if (isLocal) {
			mediaUrl = getApiUrl(
				`/admin/products/${product.id}/media-cache/local?path=${src}`
			)
		} else {
			const res = await doGet(
				`/admin/products/${product.id}/media-cache/remote?path=${src}`
			)
			const { url } = await res.json()
			mediaUrl = url
		}

		setPreview(prev => ({
			...prev,
			anchorEl,      // 👈 keep it
			mediaUrl,
			loading: false
		}))
	}


	const closePreview = () => setPreview(null)

	const normalizedFilter = useMemo(() => {
		return filter?.trim().toLowerCase()
	}, [filter])

	const fetchCache = useCallback(async () => {
		const res = await doGet(`/admin/products/${product.id}/media-cache`)
		setData(await res.json())
	}, [product])

	useEffect(() => {
		if (!product?.id) return
		fetchCache()
	}, [product?.id, fetchCache])

	const matchesFilter = (txt) => {
		if(!normalizedFilter) return true
		if(!txt) return false
		return txt?.trim().toLowerCase()?.indexOf(normalizedFilter) !== -1
	}

	const shouldShowRow = (row, contextText) => {
		if(onlyCached && !hasAnyCache(row)) return false
		if(!normalizedFilter) return true
		if(!contextText) return false
		return matchesFilter(contextText)
	}

	const getDeletePayload = row => {
		if (row.entity === 'topic') {
			return {scope: 'topic', topicId: row.id}
		}

		if (row.entity === 'question') {
			if (row.field === 'question') {
				return {scope: 'question', questionId: row.id}
			}
			return {scope: 'field', questionId: row.id, field: row.field}
		}
	}

	const renderInlineRow = (row, label, depth = 0) => {
		return (
			<Box
				key={`${row.entity}-${row.id}-${row.field}`}
				pl={depth * 3}
				sx={{
					'&:hover': {
						backgroundColor: 'grey.100',
					}
				}}
			>
				<Grid container alignItems="center" spacing={2}>
					<Grid item xs>
						{label}
					</Grid>

					<Grid item>
						<MediaIconSet
							row={row}
							onPreview={openPreview}
						/>
					</Grid>

					<Grid item>
						<IconButton
							size="small"
							disabled={!hasAnyCache(row)}
							onClick={() => setConfirm(getDeletePayload(row))}
						>
							<DeleteOutlineIcon />
						</IconButton>
					</Grid>
				</Grid>
			</Box>
		)
	}

	const renderFieldValueRow = (row, contextText, q, depth = 0) => {
		return (
			<Box
				key={`${row.entity}-${row.id}-${row.field}`}
				pl={depth * 3}
				py={0.75}
				sx={{
					'&:hover': {
						backgroundColor: 'grey.100',
					}
				}}
			>
				<Grid container alignItems="center" spacing={2}>
					<Grid item xs>
						<Typography variant="body2">
							<strong>{row.field}:</strong>{' '}
							{getFieldValue(row, q)}
						</Typography>
					</Grid>

					<Grid item>
						<MediaIconSet
							row={row}
							onPreview={openPreview}
						/>
					</Grid>

					<Grid item>
						<IconButton
							size="small"
							disabled={!hasAnyCache(row)}
							onClick={() => setConfirm(getDeletePayload(row))}
						>
							<DeleteOutlineIcon/>
						</IconButton>
					</Grid>
				</Grid>
			</Box>
		)
	}


	if (!data) return null

	return (
		<Container maxWidth="lg">
			<Box
				position="sticky"
				top={0}
				zIndex={10}
				backgroundColor={theme.palette.background.paper}
				borderBottom={"1px solid " + theme.palette.divider}
				pt={1}
				mb={1}
			>
				<Grid container alignItems="center" spacing={2}>
					<Grid item xs>
						<TextField
							size="small"
							label="Filter text"
							value={filter}
							sx={{
								mt: -1
							}}
							onChange={e => setFilter(e.target.value)}
						/>

						<FormControlLabel
							control={
								<Switch
									checked={onlyCached}
									onChange={e => setOnlyCached(e.target.checked)}
								/>
							}
							sx={{
								mt: -0.5,
								ml: 2
							}}
							label={
								<Typography>
									Only show cached
								</Typography>
							}
						/>

					</Grid>

					<Grid item>
						<Box sx={{
								width: "114px",
								textAlign: "center",
								py: 1,
								borderRadius: 1,
								borderBottomLeftRadius: 0,
								borderBottomRightRadius: 0,
								backgroundColor: theme.palette.primary.main,
								color: theme.palette.primary.contrastText,
								fontWeight: "bold"
							}}
						>
							Server
						</Box>
					</Grid>

					<Grid item>
						<Box sx={{
							width: "114px",
							textAlign: "center",
							py: 1,
							borderRadius: 1,
							borderBottomLeftRadius: 0,
							borderBottomRightRadius: 0,
							backgroundColor: theme.palette.secondary.main,
							color: theme.palette.secondary.contrastText,
							fontWeight: "bold"
						}}
						>
							Cloud
						</Box>
					</Grid>

					<Grid item>
						<Tooltip title={"Delete entire cache for this product"}>
							<IconButton
								size="small"
								onClick={() => setConfirm({scope: 'product'})}
							>
								<DeleteOutlineIcon color={"error"}/>
							</IconButton>
						</Tooltip>
					</Grid>
				</Grid>
			</Box>

			{data.topics.map(section => (
				<Box key={section.id}>
					{section.fields
						?.filter(r => r.field === 'section')
						.map(r =>
							shouldShowRow(r, getFieldValue(r, section)) &&
							renderInlineRow(
								r,
								<Box display="flex" alignItems="center" gap={1}>
									<FolderIcon fontSize="small"/>
									<Typography fontWeight={600}>
										{getFieldValue(r, section)}
									</Typography>
								</Box>,
								0
							)
						)
					}

					{section.topics?.map(topic => (
						<Box key={topic.id} mt={2}>
							{topic.fields
								?.filter(r => r.field === 'title')
								.map(r =>
									shouldShowRow(r, getFieldValue(r, topic)) &&
									renderInlineRow(
										r,
										<Box display="flex" alignItems="center" gap={1}>
											<FolderIcon fontSize="small"/>
											<Typography fontWeight={600}>
												{getFieldValue(r, topic)}
											</Typography>
										</Box>,
										1
									)
								)
							}

							{topic.questions?.map(q => (
								<Box key={q.id} mt={1}>
									{q.fields
										.filter(r => r.field === 'question')
										.map(r =>
											shouldShowRow(r, getFieldValue(r, q)) &&
											renderInlineRow(
												r,
												getFieldValue(r, q),
												2
											)
										)
									}

									{q.fields
										.filter(r => r.field !== 'question')
										.map(r =>
											shouldShowRow(r, getFieldValue(r, q)) &&
											renderFieldValueRow(
												r,
												getFieldValue(r, q),
												q,
												3
											)
										)
									}
								</Box>
							))}
						</Box>
					))}
				</Box>
			))}

			<ConfirmDialog
				open={Boolean(confirm)}
				title="Confirm cache deletion"
				content="This will remove cached media files. This cannot be undone."
				onCancel={() => setConfirm(null)}
				onConfirm={async () => {
					await doDelete(
						`/admin/products/${product.id}/media-cache`,
						{body: JSON.stringify(confirm)}
					)
					setConfirm(null)
					await fetchCache()
				}}
			/>
			<Popover
				open={Boolean(preview)}
				anchorEl={preview?.anchorEl}
				onClose={closePreview}
				anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
			>
				<Box p={2} maxWidth={480}>
					{preview?.loading && (
						<Typography variant="body2">Loading…</Typography>
					)}

					{!preview?.loading && preview?.type === 'image' && (
						<Box position="relative">
							<img
								src={preview.mediaUrl}
								style={{ maxWidth: '100%', borderRadius: 4 }}
							/>

							<IconButton
								size="small"
								onClick={() => setFullscreen(true)}
								sx={{
									position: 'absolute',
									top: 8,
									right: 8,
									backgroundColor: 'rgba(0,0,0,0.4)',
									color: 'white',
									'&:hover': {
										backgroundColor: 'rgba(0,0,0,0.6)'
									}
								}}
							>
								<Fullscreen fontSize="small" />
							</IconButton>
						</Box>
					)}

					{!preview?.loading && preview?.type === 'audio' && (
						<audio controls src={preview.mediaUrl} />
					)}

					{!preview?.loading && preview?.type === 'video' && (
						<video
							controls
							src={preview.mediaUrl}
							style={{ maxWidth: '100%', borderRadius: 4 }}
						/>
					)}

				</Box>
			</Popover>
			<Dialog
				open={fullscreen}
				onClose={() => setFullscreen(false)}
				fullScreen
			>
				<Box
					display="flex"
					alignItems="center"
					justifyContent="center"
					height="100%"
					bgcolor="black"
				>
					<IconButton
						onClick={() => setFullscreen(false)}
						sx={{ position: 'absolute', top: 16, right: 16, color: 'white' }}
					>
						<Close />
					</IconButton>

					<img
						src={preview?.mediaUrl}
						style={{ maxWidth: '100%', maxHeight: '100%' }}
					/>
				</Box>
			</Dialog>


		</Container>
	)
}

const MediaIcon = ({
	                   available,
	                   onClick,
	                   Outlined,
	                   Filled,
	                   color
                   }) => {
	const [hover, setHover] = useState(false)

	const Icon = hover && available ? Filled : Outlined

	return (
		<IconButton
			size="small"
			disabled={!available}
			onClick={available ? onClick : undefined}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			sx={{
				color: available ? color : 'divider'
			}}
		>
			<Icon fontSize="small" sx={{visibility: available ? 'visible' : 'hidden' }}/>
		</IconButton>
	)
}

const MediaIconSet = ({row, onPreview}) => {
	const theme = useTheme()

	const pickSrc = (type, isLocal) => {
		if (type === 'image') {
			return isLocal ? row.image?.localKey : row.image?.remoteKey
		}

		const v = Object.values(row.variants || {})[0]
		if (!v) return null

		if (type === 'audio') {
			return isLocal ? v.audio?.localKey : v.audio?.remoteKey
		}

		if (type === 'video') {
			return isLocal ? v.video?.localKey : v.video?.remoteKey
		}
	}

	const renderSet = (isLocal, color) => {
		const image = isLocal ? row.image?.local : row.image?.remote
		const audio = Object.values(row.variants || {}).some(v =>
			isLocal ? v.audio?.local : v.audio?.remote
		)
		const video = Object.values(row.variants || {}).some(v =>
			isLocal ? v.video?.local : v.video?.remote
		)

		return (
			<Box
				display="flex"
				gap={0.5}
				px={1}
				py={0.25}
				sx={{
					border: `1px solid transparent`,
					borderRadius: 1,
					'&:hover': {
						border: `1px solid ${theme.palette.divider}`
					},

				}}
			>
				<MediaIcon
					available={image}
					color={color}
					Outlined={ImageOutlinedIcon}
					Filled={ImageIcon}
					onClick={e => onPreview(e, 'image', pickSrc('image', isLocal), isLocal)}
				/>
				<MediaIcon
					available={audio}
					color={color}
					Outlined={AudiotrackOutlinedIcon}
					Filled={AudiotrackIcon}
					onClick={e => onPreview(e, 'audio', pickSrc('audio', isLocal), isLocal)}
				/>
				<MediaIcon
					available={video}
					color={color}
					Outlined={MovieOutlinedIcon}
					Filled={MovieIcon}
					onClick={e => onPreview(e, 'video', pickSrc('video', isLocal), isLocal)}
				/>
			</Box>
		)
	}

	return (
		<Box display="flex" gap={1.5}>
			{renderSet(true, 'primary.main')}
			{renderSet(false, 'secondary.main')}
		</Box>
	)
}

export default ProductMediaCache
